import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Leaf, Loader2, Utensils, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScoreDial({ score }) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const color = s >= 80 ? "#16A34A" : s >= 60 ? "#D97706" : "#DC2626";
  const dash = (s / 100) * 283;
  return (
    <div className="relative w-28 h-28">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="45" fill="none" stroke="#F4EFE6" strokeWidth="8" />
        <circle cx="50" cy="50" r="45" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={`${dash} 283`} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <p className="font-serif-display text-3xl font-semibold" style={{ color: "#1C2826" }}>{s}</p>
      </div>
    </div>
  );
}

function SeverityBadge({ level }) {
  const cls = level === "severe" ? "severity-severe" : level === "moderate" ? "severity-moderate" : "severity-mild";
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold border ${cls}`}>
      {level}
    </span>
  );
}

export default function SharedReport() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/reports/${token}`);
        setReport(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Report not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return <div className="py-24 text-center"><Loader2 className="animate-spin mx-auto" style={{ color: "#2D5A4C" }} /></div>;
  }
  if (err || !report) {
    return (
      <div className="max-w-lg mx-auto py-24 text-center card-warm p-8">
        <AlertTriangle size={28} className="mx-auto mb-2" style={{ color: "#BF5832" }} />
        <p className="text-sm" style={{ color: "#5C6B66" }}>{err}</p>
      </div>
    );
  }

  const s = report.summary || {};

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 fade-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: "#2D5A4C" }}>
            <Leaf size={16} color="white" />
          </div>
          <span className="font-serif-display text-xl font-semibold" style={{ color: "#1C2826" }}>Harmonix</span>
        </div>
        <Button
          onClick={() => window.print()}
          className="btn-brand rounded-full h-10"
          data-testid="shared-print-btn"
        >
          <Printer size={14} className="mr-2" /> Print / Save as PDF
        </Button>
      </div>

      <div className="card-warm p-6 sm:p-8">
        <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#5C6B66" }}>
          Interaction Report {report.owner_name ? `· ${report.owner_name}` : ""}
        </p>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-1" style={{ color: "#1C2826" }}>
          Regimen Analysis
        </h1>
        <p className="mt-1 text-xs" style={{ color: "#5C6B66" }}>
          Generated {new Date(report.created_at).toLocaleString()}
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-5 items-start">
          <ScoreDial score={s.harmony_score} />
          <p className="text-sm leading-relaxed flex-1" style={{ color: "#1C2826" }}>{s.overall_summary}</p>
        </div>

        <div className="mt-6 rounded-xl p-3 text-[11px] leading-relaxed"
          style={{ background: "#FDF0EA", border: "1px solid #E5DEC9", color: "#5C3B2A" }}>
          <div className="flex gap-2 items-start">
            <AlertTriangle size={14} style={{ color: "#BF5832" }} className="mt-0.5 shrink-0" />
            <span>
              <strong>Medical notice:</strong> This report is informational only. Discuss all changes with a qualified physician or pharmacist.
            </span>
          </div>
        </div>
      </div>

      {/* Medications */}
      <div className="card-warm p-6 sm:p-8 mt-6">
        <h2 className="font-serif-display text-2xl font-medium mb-4" style={{ color: "#1C2826" }}>Regimen</h2>
        <ul className="space-y-2">
          {(report.medications || []).map((m, i) => (
            <li key={i} className="text-sm" style={{ color: "#1C2826" }}>
              <strong>{m.name}</strong>
              <span style={{ color: "#5C6B66" }}>
                {" · "}{m.category}{m.dosage ? ` · ${m.dosage}` : ""}{m.frequency ? ` · ${m.frequency}` : ""}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Interactions */}
      <div className="card-warm p-6 sm:p-8 mt-6">
        <h2 className="font-serif-display text-2xl font-medium mb-4" style={{ color: "#1C2826" }}>Cross-interactions</h2>
        {(s.interactions || []).length === 0 ? (
          <p className="text-sm" style={{ color: "#5C6B66" }}>No interactions detected.</p>
        ) : (
          <div className="space-y-3">
            {s.interactions.map((i, idx) => (
              <div key={idx} className="p-4 rounded-2xl border" style={{ borderColor: "#E5DEC9" }}>
                <div className="flex items-center gap-2 flex-wrap">
                  <SeverityBadge level={i.severity} />
                  <span className="font-semibold text-sm" style={{ color: "#1C2826" }}>
                    {(i.items || []).join(" + ")}
                  </span>
                </div>
                {i.mechanism && <p className="mt-2 text-sm" style={{ color: "#1C2826" }}><strong>Mechanism:</strong> {i.mechanism}</p>}
                {i.effect && <p className="mt-1 text-sm" style={{ color: "#5C6B66" }}><strong>Effect:</strong> {i.effect}</p>}
                {i.recommendation && (
                  <p className="mt-2 text-sm rounded-lg p-2" style={{ background: "#F4EFE6", color: "#23473C" }}>
                    <strong>Advice:</strong> {i.recommendation}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Symptoms */}
      {(s.potential_symptoms || []).length > 0 && (
        <div className="card-warm p-6 sm:p-8 mt-6">
          <h2 className="font-serif-display text-2xl font-medium mb-4" style={{ color: "#1C2826" }}>Possible symptoms</h2>
          <ul className="space-y-1 text-sm" style={{ color: "#1C2826" }}>
            {s.potential_symptoms.map((sym, i) => (
              <li key={i}>• <strong>{sym.symptom}</strong> ({sym.likelihood}) {sym.cause ? `— ${sym.cause}` : ""}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Food flags */}
      {(s.food_alcohol_flags || []).length > 0 && (
        <div className="card-warm p-6 sm:p-8 mt-6">
          <div className="flex items-center gap-2 mb-4">
            <Utensils size={18} style={{ color: "#BF5832" }} />
            <h2 className="font-serif-display text-2xl font-medium" style={{ color: "#1C2826" }}>Food, alcohol & lifestyle</h2>
          </div>
          <ul className="space-y-2">
            {s.food_alcohol_flags.map((f, i) => (
              <li key={i} className={`p-3 rounded-xl border severity-${f.severity}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <SeverityBadge level={f.severity} />
                  <span className="font-semibold text-sm" style={{ color: "#1C2826" }}>{f.factor}</span>
                </div>
                <p className="mt-1 text-sm" style={{ color: "#5C6B66" }}>{f.concern}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      {(s.recommendations || []).length > 0 && (
        <div className="card-warm p-6 sm:p-8 mt-6" style={{ background: "#E8F0EC" }}>
          <h2 className="font-serif-display text-2xl font-medium mb-3" style={{ color: "#1C2826" }}>Smart next steps</h2>
          <ul className="space-y-2 text-sm" style={{ color: "#23473C" }}>
            {s.recommendations.map((r, i) => (
              <li key={i} className="flex gap-2"><span>•</span><span>{r}</span></li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-center text-xs mt-8 print:hidden" style={{ color: "#5C6B66" }}>
        Generated by Harmonix Health · This tool does not provide medical advice.
      </p>
    </div>
  );
}
